import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Download } from "lucide-react";
import { PageHeader, KV } from "@/pages/Accounts";

export default function ContactListDetailPage() {
  const { id } = useParams();
  const [cl, setCl] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/contact-lists/${id}`);
      setCl(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, [id]);

  if (!cl) {
    return (
      <div className="p-8">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild data-testid="cl-back">
          <Link to={`/projects/${cl.project_id}`}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to project
          </Link>
        </Button>
      </div>
      <PageHeader
        title={cl.name}
        description={
          <span className="font-mono text-xs">
            {cl.source_filename} · {(cl.source_bytes / 1024).toFixed(1)} KB · status:{" "}
            <Badge
              variant={
                cl.status === "ready"
                  ? "default"
                  : cl.status === "failed"
                  ? "destructive"
                  : "secondary"
              }
              className="ml-1"
            >
              {cl.status}
            </Badge>
          </span>
        }
      />

      <Tabs defaultValue="summary" data-testid="cl-tabs">
        <TabsList>
          <TabsTrigger value="summary" data-testid="tab-cl-summary">Summary</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-cl-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="invalid" data-testid="tab-cl-invalid">Invalid rows</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-cl-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-4 space-y-4">
          <Card data-testid="cl-stats-card">
            <CardHeader>
              <CardTitle className="text-base">Row stats</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <KV k="Total rows" v={cl.row_stats.total_rows} />
              <KV k="Valid rows" v={cl.row_stats.valid_rows} />
              <KV k="Invalid rows" v={cl.row_stats.invalid_rows} />
              <KV k="Duplicates dropped" v={cl.row_stats.duplicate_rows_dropped} />
              <KV k="Unique phones" v={cl.row_stats.unique_phones} />
              <KV k="Phone column" v={<span className="font-mono">{cl.phone_column}</span>} />
              <KV k="Duplicate policy" v={cl.duplicate_policy} />
            </CardContent>
          </Card>
          <Card data-testid="cl-facets-card">
            <CardHeader>
              <CardTitle className="text-base">Filter facets</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {Object.keys(cl.filter_facets).length === 0 && (
                <div className="text-xs text-muted-foreground">
                  No filterable columns configured.
                </div>
              )}
              {Object.entries(cl.filter_facets).map(([col, vals]) => (
                <div key={col} data-testid={`facet-${col}`}>
                  <div className="text-xs font-medium">
                    {col}{" "}
                    <span className="text-muted-foreground">({vals.length} distinct)</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {vals.slice(0, 10).map((v) => (
                      <Badge variant="outline" key={v} className="text-[10px]">
                        {v}
                      </Badge>
                    ))}
                    {vals.length > 10 && (
                      <Badge variant="secondary" className="text-[10px]">
                        +{vals.length - 10} more
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <ContactsTab cl={cl} />
        </TabsContent>
        <TabsContent value="invalid" className="mt-4">
          <InvalidRowsTab clId={id} />
        </TabsContent>
        <TabsContent value="settings" className="mt-4">
          <SettingsTab cl={cl} reload={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ContactsTab({ cl }) {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  const active = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(selected).filter(([, v]) => Array.isArray(v) && v.length > 0),
      ),
    [selected],
  );

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contact-lists/${cl.id}/contacts/search`, {
        page,
        per_page: perPage,
        search: search || undefined,
        is_valid: true,
        filters: active,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    load();
  }, [cl.id, page, JSON.stringify(active)]);

  const exportFiltered = async () => {
    try {
      const resp = await api.post(
        `/contact-lists/${cl.id}/export`,
        { page: 1, per_page: 200, is_valid: true, search: search || undefined, filters: active },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cl.name}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cols = cl.filterable_columns || [];
  const facets = cl.filter_facets || {};

  return (
    <div className="space-y-4">
      <Card data-testid="cl-filters-card">
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-3">
            <div className="min-w-[240px] flex-1">
              <Input
                placeholder="Search phone (regex-safe substring)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onBlur={() => {
                  setPage(1);
                  load();
                }}
                data-testid="cl-search-input"
              />
            </div>
            <Button variant="outline" onClick={exportFiltered} data-testid="cl-export">
              <Download className="mr-2 h-4 w-4" />
              Export filtered CSV
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {cols.map((c) => (
              <div key={c} data-testid={`cl-filter-${c}`}>
                <div className="text-xs font-medium mb-1">{c}</div>
                <div className="border rounded-md p-2 max-h-32 overflow-auto space-y-1">
                  {(facets[c] || []).slice(0, 50).map((v) => (
                    <label
                      key={v}
                      className="flex items-center gap-2 text-xs cursor-pointer"
                    >
                      <Checkbox
                        checked={(selected[c] || []).includes(v)}
                        onCheckedChange={(chk) => {
                          const cur = selected[c] || [];
                          const next = chk ? [...cur, v] : cur.filter((x) => x !== v);
                          setPage(1);
                          setSelected({ ...selected, [c]: next });
                        }}
                        data-testid={`cl-facet-${c}-${v}`}
                      />
                      {v}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="cl-contacts-table-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">
            Contacts {busy && <Loader2 className="inline h-3 w-3 animate-spin ml-1" />}
          </CardTitle>
          <div className="text-xs text-muted-foreground" data-testid="cl-total">
            total matched: <span className="font-mono">{total}</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Phone</TableHead>
                {cols.map((c) => (
                  <TableHead key={c}>{c}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={cols.length + 1} className="text-center py-6 text-xs text-muted-foreground">
                    No matches.
                  </TableCell>
                </TableRow>
              )}
              {items?.map((it) => (
                <TableRow key={it.id} data-testid={`cl-contact-row-${it.phone_e164}`}>
                  <TableCell className="font-mono text-xs">{it.phone_e164}</TableCell>
                  {cols.map((c) => (
                    <TableCell key={c} className="text-xs">
                      {String(it.data?.[c] ?? "")}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex justify-between items-center">
        <div className="text-xs text-muted-foreground">
          Page {page} · {perPage} per page
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            data-testid="cl-prev-page"
          >
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={page * perPage >= total}
            data-testid="cl-next-page"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

function InvalidRowsTab({ clId }) {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(
          `/contact-lists/${clId}/invalid-rows?page=${page}&per_page=50`,
        );
        setItems(data.items);
        setTotal(data.total);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    };
    load();
  }, [clId, page]);

  return (
    <Card data-testid="cl-invalid-card">
      <CardHeader>
        <CardTitle className="text-base">Invalid rows ({total})</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Row #</TableHead>
              <TableHead>Raw phone</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Snapshot</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items?.map((it) => (
              <TableRow key={it.id} data-testid={`cl-invalid-row-${it.id}`}>
                <TableCell className="font-mono text-xs">{it.row_index}</TableCell>
                <TableCell className="font-mono text-xs">{it.phone_raw || "—"}</TableCell>
                <TableCell className="text-xs">{it.invalid_reason}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[400px] truncate">
                  {JSON.stringify(it.data)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function SettingsTab({ cl, reload }) {
  const [name, setName] = useState(cl.name);
  const [desc, setDesc] = useState(cl.description || "");

  const save = async () => {
    try {
      await api.patch(`/contact-lists/${cl.id}`, { name, description: desc });
      toast.success("Saved");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const retry = async () => {
    try {
      await api.post(`/contact-lists/${cl.id}/retry-parse`);
      toast.success("Re-parse started");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Card data-testid="cl-settings-card">
      <CardHeader>
        <CardTitle className="text-base">Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          <label className="text-xs font-medium">Name</label>
          <Input
            data-testid="cl-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium">Description</label>
          <Input
            data-testid="cl-desc-input"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={save} data-testid="cl-save">Save</Button>
          <Button variant="outline" onClick={retry} data-testid="cl-retry-parse">
            <Loader2 className="mr-2 h-3 w-3" />
            Retry parse (admin only)
          </Button>
        </div>
        {cl.parse_error && (
          <div className="text-xs text-destructive font-mono">
            last error: {cl.parse_error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
